import React, { useMemo } from "react";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useAlarms } from "../useAlarms";
import { useCoordinates } from "./common";
import { isDemoData } from "../boardData";
import { DEMO_MAP_TOKEN, demoMapStyle } from "../demo/demoBasemap";
import DemoPlaces from "../demo/DemoPlaces";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
// Previews draw an offline basemap and need no real token.
const mapToken = () => (isDemoData() ? DEMO_MAP_TOKEN : MAPBOX_TOKEN);

function alarmLatLng(a) {
  const lat = Number(a.Latitude ?? a.latitude ?? a.lat);
  const lng = Number(a.Longitude ?? a.longitude ?? a.lng ?? a.long);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

// Live incident view. On an active alarm it centers the map on the call and shows
// an incident panel; idle, it shows the station area. Falls back to a panel-only
// view when no Mapbox token is configured.
const AlarmMapWidget = ({ config = {}, agency, board }) => {
  const alarms = useAlarms(agency);
  const station = useCoordinates(agency, board);
  const primary = alarms[0];
  const primaryLoc = primary ? alarmLatLng(primary) : null;

  const center = useMemo(() => {
    if (primaryLoc) return primaryLoc;
    if (station.lat != null && station.lng != null) return { lat: station.lat, lng: station.lng };
    return null;
  }, [primaryLoc, station.lat, station.lng]);

  const incidentPanel = (
    <div className="sb-incident-panel">
      {primary ? (
        <>
          <div className="sb-incident-type">
            {primary.CallType || primary.callType || primary.DispatchCode || "Active Incident"}
          </div>
          {(primary.Address || primary.address) && (
            <div className="sb-incident-addr">
              {primary.Address || primary.address}
              {primary.Town ? `, ${primary.Town}` : ""}
            </div>
          )}
          {(primary.CallSubType || primary.LocationComments || primary.locationName) && (
            <div className="sb-incident-sub">
              {primary.CallSubType || primary.locationName || primary.LocationComments}
            </div>
          )}
          {alarms.length > 1 && (
            <div className="sb-incident-more">+{alarms.length - 1} more active</div>
          )}
        </>
      ) : (
        <div className="sb-incident-clear">No active incidents</div>
      )}
    </div>
  );

  const showMap = mapToken() && center;

  return (
    <div className={`sb-alarmmap${primary ? " active" : ""}`}>
      {showMap ? (
        <Map
          reuseMaps
          mapboxAccessToken={mapToken()}
          initialViewState={{ latitude: center.lat, longitude: center.lng, zoom: primary ? 14 : 12 }}
          mapStyle={isDemoData() ? demoMapStyle() : config.mapStyle || "mapbox://styles/mapbox/dark-v11"}
          style={{ width: "100%", height: "100%" }}
          attributionControl={false}
        >
          <DemoPlaces />
          {alarms.map((a) => {
            const loc = alarmLatLng(a);
            if (!loc) return null;
            return (
              <Marker key={a.id} latitude={loc.lat} longitude={loc.lng}>
                <div className="sb-alarm-marker" />
              </Marker>
            );
          })}
          {station.lat != null && station.lng != null && (
            <Marker latitude={station.lat} longitude={station.lng}>
              <div className="sb-station-marker" />
            </Marker>
          )}
        </Map>
      ) : (
        <div className="sb-alarmmap-nomap" />
      )}
      {incidentPanel}
    </div>
  );
};

export default AlarmMapWidget;
